import { ProgramAssessmentFields } from "../../components/dtp/ProgramAssessmentFields";
import { setStoreContext, trackStoreEvent } from '../../lib/dtpAnalytics';
import { track } from '../../lib/analytics';
import { programErrors } from "../../components/dtp/ProgramQuestions";
import type { PublicIntakeProgram, IntakeProgramSubmission, StoreEntryFlow } from "@bloom/shared";
import { combinedQualificationQuestions, qualificationSubmissions } from '../../lib/combinedQualification';
import { useQualificationDraft } from '../../hooks/useQualificationDraft';
import { defaultQualificationQuestions } from '@bloom/shared';
import { ProductQualificationQuestions, type CheckoutQualificationProduct } from '../../components/dtp/ProductQualificationQuestions';
import { dtpPath, useDtpParams, patientSignInDestination } from '../../lib/clinicSite';
import { useState, useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Loader2, CreditCard, ArrowLeft, Trash2,
  Phone, ShieldCheck, CheckSquare, ArrowRight
} from 'lucide-react';
import { loadHyper } from '@juspay-tech/hyper-js';
import { HyperElements, UnifiedCheckout } from '@juspay-tech/react-hyper-js';
import { dtpApi } from '../../lib/dtpApi';
import { dobInputBounds, dobRangeError } from '../../utils/dateUtils';
import { useDtpCartStore } from '../../store/useDtpCartStore';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { getCurrentTenantConfig } from '../../config/tenant';
import { ConsentType } from '@bloom/shared';

type CheckoutStep = 'otp-phone' | 'otp-verify' | 'signup' | 'medical' | 'review' | 'payment';

interface SignupFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  consentCompounding: boolean;
  consentTelehealth: boolean;
  consentHipaa: boolean;
  consentTos: boolean;
}

type AnimalSpeciesOpt = 'dog' | 'cat' | 'horse' | 'bird' | 'reptile' | 'exotic' | 'other';
type AnimalSexOpt = 'male' | 'female' | 'male_neutered' | 'female_spayed' | 'unknown';

// Vet (animal) equivalent of the medical screening — the pet being treated.
interface AnimalDetailsData {
  name: string;
  species: AnimalSpeciesOpt | '';
  breed: string;
  sex: AnimalSexOpt | '';
  weightLbs: string;
  ageYears: string;
}

const INITIAL_ANIMAL: AnimalDetailsData = {
  name: '', species: '', breed: '', sex: '', weightLbs: '', ageYears: '',
};

const SPECIES_OPTIONS: { value: AnimalSpeciesOpt; label: string }[] = [
  { value: 'dog', label: 'Dog' },
  { value: 'cat', label: 'Cat' },
  { value: 'horse', label: 'Horse' },
  { value: 'bird', label: 'Bird' },
  { value: 'reptile', label: 'Reptile' },
  { value: 'exotic', label: 'Exotic' },
  { value: 'other', label: 'Other' },
];

const ANIMAL_SEX_OPTIONS: { value: AnimalSexOpt; label: string }[] = [
  { value: 'male', label: 'Male (intact)' },
  { value: 'female', label: 'Female (intact)' },
  { value: 'male_neutered', label: 'Male (neutered)' },
  { value: 'female_spayed', label: 'Female (spayed)' },
  { value: 'unknown', label: 'Unknown' },
];

function computeBmi(heightInches: number, weightLbs: number): number {
  return Math.round(((weightLbs / (heightInches * heightInches)) * 703) * 10) / 10;
}

function bmiCategory(bmi: number): { label: string; color: string } {
  if (bmi < 18.5) return { label: 'Underweight', color: 'bg-blue-100 text-blue-700' };
  if (bmi < 25)   return { label: 'Normal', color: 'bg-green-100 text-green-700' };
  if (bmi < 30)   return { label: 'Overweight', color: 'bg-amber-100 text-amber-700' };
  return { label: 'Obese', color: 'bg-red-100 text-red-700' };
}

const DEFAULT_CONSENT_TEXTS = {
  compounding: 'I understand that the medications I am ordering are compounded medications prepared by a licensed compounding pharmacy, and that compounded medications are not FDA-approved.',
  telehealth: 'I consent to receiving telehealth services and understand that a qualified healthcare provider may review my order.',
  hipaa: 'I acknowledge that I have read and understand the Notice of Privacy Practices (HIPAA) and consent to the use and disclosure of my health information as described.',
  tos: 'I agree to the Terms of Service and understand the policies regarding orders, shipping, returns, and refunds.'
};

export function DtpCheckout() {
  const { physicianId } = useDtpParams<{ physicianId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const portalDestination = patientSignInDestination(physicianId, location.state?.patientReturnTo);
  // intent=membership: the user came here from the membership page to sign in,
  // not to buy items in the cart. Suppress the empty-cart guard, and once they
  // authenticate, send them back to /membership instead of stepping into the
  // medical/review/payment flow.
  const intent = searchParams.get('intent');
  const isMembershipSignin = intent === 'membership';
  const isPortalSignin = intent === 'portal';
  const isSigninOnly = isMembershipSignin || isPortalSignin;
  const { items, removeItem, clinicState, allowedStates, isVet } = useDtpCartStore();
  const { isAuthenticated, patient, signupToken, phone: storedPhone, setAuth, setSignupPending } = useDtpAuthStore();

  // Shipping-state coverage for this clinic (own licenses ∪ telemed network).
  //  - exactly one state  -> lock the field to it (single-state clinic)
  //  - several states      -> render a dropdown limited to them (multi-state / telemed)
  //  - none (legacy/no data) -> leave it as free entry
  const stateOptions = allowedStates.length > 0
    ? allowedStates
    : (clinicState ? [clinicState] : []);
  const lockedState = stateOptions.length === 1 ? stateOptions[0] : null;
  const multiStateAllowed = stateOptions.length > 1;
  const coverageAllows = (s?: string | null): boolean =>
    !!s && (stateOptions.length === 0 || stateOptions.includes(s));
  const initialShippingState =
    lockedState || (coverageAllows(patient?.address?.state) ? (patient?.address?.state || '') : '');

  // Determine initial step — returning patients go to medical screening first
  const getInitialStep = (): CheckoutStep => {
    if (isAuthenticated) return 'medical';
    if (signupToken) return 'signup';
    return 'otp-phone';
  };

  const [step, setStep] = useState<CheckoutStep>(getInitialStep);
  const [phone, setPhone] = useState(storedPhone || '');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const draftScope = JSON.stringify([physicianId, patient?._id || signupToken || 'anonymous']);
  const { answers: qualificationAnswers, setAnswers: setQualificationAnswers, measurements: medical, setMeasurements: setMedical, transferTo, programs: programDrafts, setPrograms: setProgramDrafts } = useQualificationDraft(draftScope);
  const [medicalErrors, setMedicalErrors] = useState<string[]>([]);
  const [intakePrograms, setIntakePrograms] = useState<PublicIntakeProgram[]>([]);
  const [entryFlow, setEntryFlow] = useState<StoreEntryFlow>();
  const programSubmission = (p: PublicIntakeProgram): IntakeProgramSubmission => {
    if (programDrafts[p.id]?.revision === p.revision) return programDrafts[p.id];
    // A dose picked with Change dose arrives on the cart item: start on that plan.
    const picked = items.find(item => item.plan?.programId === p.id)?.plan;
    const plan = picked && p.dosagePlans?.find(plan => plan.id === picked.planId && plan.path === picked.path);
    return { programId: p.id, revision: p.revision, entry: "direct", answers: {}, ...(plan && { path: plan.path, planId: plan.id }) };
  };
  const [qualificationProducts, setQualificationProducts] = useState<CheckoutQualificationProduct[]>([]);
  const [qualificationReady, setQualificationReady] = useState(false);
  const productKey = items.map(item => item.productId).sort().join(',');
  useEffect(() => {
    let active = true;
    setQualificationReady(false);
    if (!physicianId || isVet || !productKey) { setQualificationReady(true); return; }
    dtpApi.getCatalog(physicianId, { fresh: true }).then((res: any) => {
      if (!active) return;
      const selected = (res.data?.products || []).filter((p: any) => productKey.split(',').includes(p._id));
      if (!res.success || selected.length !== productKey.split(',').length || selected.some((p: any) => !p.qualification)) throw new Error('One or more products are unavailable. Return to the store and refresh your selection.');
      setQualificationProducts(selected);
      setIntakePrograms((res.data?.intakePrograms || []).filter((p: PublicIntakeProgram) => p.productIds.some(id => productKey.split(",").includes(id))));
      setEntryFlow(res.data?.clinic?.entryFlow);
      setQualificationReady(true);
    }).catch(() => { if (active) setError('Could not load qualification questions. Refresh before continuing.'); });
    return () => { active = false; };
  }, [physicianId, productKey, isVet]);
  // Loaded for every clinic (vet included): the order API requires the
  // acknowledgment whenever the deploy names a processor.
  useEffect(() => {
    if (!physicianId) return;
    let active = true;
    dtpApi.getCatalog(physicianId)
      .then((res: any) => {
        if (!active) return;
        setPaymentProcessor(res.data?.clinic?.paymentProcessor || undefined);
        setStoreContext(res.data?.clinic?.physicianId, res.data?.clinic?.storefrontVariant);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [physicianId]);
  // Checkout funnel: one event per step shown (sign-in-only visits aren't checkouts).
  useEffect(() => {
    if (!isSigninOnly) trackStoreEvent('checkout_step', { step });
  }, [step, isSigninOnly]);
  // Once per visit to checkout with something in the cart.
  const checkoutStarted = useRef(false);
  useEffect(() => {
    if (checkoutStarted.current || isSigninOnly || items.length === 0) return;
    checkoutStarted.current = true;
    track('checkout_started', { itemCount: items.length, authenticated: isAuthenticated });
  }, [items.length, isSigninOnly, isAuthenticated]);
  const needsMeasurements = qualificationProducts.some(p => p.qualification.requiresMeasurements);
  const needsSex = needsMeasurements || qualificationProducts.some(p => p.qualification.questions.some(q => q.sex));
  const buildQualificationAnswers = () => qualificationSubmissions(qualificationProducts, qualificationAnswers, medical.biologicalSex);

  // Vet clinics collect the animal (pet) instead of the human medical screening.
  const [animal, setAnimal] = useState<AnimalDetailsData>(INITIAL_ANIMAL);

  // Review step: shipping address (editable, persisted to patient record)
  const [shippingAddress, setShippingAddress] = useState({
    street: patient?.address?.street || '',
    street2: patient?.address?.street2 || '',
    city: patient?.address?.city || '',
    state: initialShippingState,
    zipCode: patient?.address?.zipCode || '',
  });

  // Review step: billing address
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] = useState({
    street: '', street2: '', city: '', state: '', zipCode: '',
  });

  // Review step: consents (required every order)
  const [reviewConsents, setReviewConsents] = useState({
    compounding: false, telehealth: false, hipaa: false, tos: false,
  });
  // Company that processes the card payment on this deploy (from the catalog).
  // When set, the patient must tick an explicit acknowledgment on every order —
  // never pre-seeded from signup.
  const [paymentProcessor, setPaymentProcessor] = useState<string>();
  const [processorAcknowledged, setProcessorAcknowledged] = useState(false);

  // True once a new patient completes the signup form — where they already
  // gave the address and consents. Returning patients skip signup, so they
  // still get the full review fields. Used to avoid re-asking at review.
  const [completedSignup, setCompletedSignup] = useState(false);
  // Lets a new patient expand the pre-filled shipping summary back into an
  // editable form instead of seeing the full form by default.
  const [editingShipping, setEditingShipping] = useState(false);

  // Sync shipping address when patient data loads (e.g. after signup or auth restore)
  useEffect(() => {
    if (patient?.address) {
      setShippingAddress(prev => ({
        street: prev.street || patient.address?.street || '',
        street2: prev.street2 || patient.address?.street2 || '',
        city: prev.city || patient.address?.city || '',
        state: lockedState || prev.state || (coverageAllows(patient.address?.state) ? (patient.address?.state || '') : ''),
        zipCode: prev.zipCode || patient.address?.zipCode || '',
      }));
    }
  }, [patient?.address, clinicState, lockedState]);

  // Seed animal details from the patient record for a returning vet patient.
  useEffect(() => {
    const a = (patient as any)?.animal;
    if (a) {
      setAnimal(prev => ({
        name: prev.name || a.name || '',
        species: prev.species || a.species || '',
        breed: prev.breed || a.breed || '',
        sex: prev.sex || a.sex || '',
        weightLbs: prev.weightLbs || (a.weightLbs != null ? String(a.weightLbs) : ''),
        ageYears: prev.ageYears || (a.ageYears != null ? String(a.ageYears) : ''),
      }));
    }
  }, [(patient as any)?.animal]);

  // Payment state
  const [orderCreated, setOrderCreated] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [orderId, setOrderId] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [hyperPromise, setHyperPromise] = useState<any>(null);

  const subtotal = items.reduce((sum, i) => sum + i.price, 0);

  // Whether we already have a usable shipping address (e.g. captured at signup
  // and synced from the patient record) — drives the summary-vs-form view.
  const shippingComplete = !!(
    shippingAddress.street.trim() &&
    shippingAddress.city.trim() &&
    shippingAddress.state.trim() &&
    shippingAddress.zipCode.trim()
  );

  // Redirect to shop if cart is empty and no order in progress.
  // Skip when the user is on a membership sign-in detour — they have no cart
  // by design and should stay here long enough to complete OTP.
  useEffect(() => {
    if (isSigninOnly) return;
    if (items.length === 0 && !orderCreated) {
      navigate(dtpPath(physicianId), { replace: true });
    }
  }, [items.length, orderCreated, navigate, physicianId, isSigninOnly]);

  // If auth state changes (e.g. persisted session restored), jump to medical
  // screening — unless the user came here only to sign in for membership, in
  // which case bounce them straight back to the membership page.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isPortalSignin) { navigate(portalDestination, { replace: true }); return; }
    if (isMembershipSignin) {
      // Signal the membership page to resume straight into card setup with the
      // saved draft, instead of dropping the patient back on the picker to
      // re-confirm a selection they already made before signing in.
      navigate(dtpPath(physicianId, `/membership?resume=card`), { replace: true });
      return;
    }
    if (['otp-phone', 'otp-verify'].includes(step)) {
      setStep('medical');
    }
  }, [isAuthenticated, isMembershipSignin, isPortalSignin, portalDestination, navigate, physicianId, step]);

  // The reverse: a 401 anywhere logs the patient out (dtpApi interceptor), which
  // also drops a pending signup token. Past the OTP steps the page would then
  // submit a token-less signup ("Signup token is required"), so send them back
  // to verify their phone instead.
  useEffect(() => {
    if (isAuthenticated || signupToken) return;
    if (['otp-phone', 'otp-verify'].includes(step)) return;
    setStep('otp-phone');
    setOtpCode('');
    setError('Your session expired. Please verify your phone number again to continue.');
  }, [isAuthenticated, signupToken, step]);

  // --- OTP Handlers ---

  const handleRequestOtp = async () => {
    if (!phone.trim() || !physicianId) return;
    setLoading(true);
    setError('');
    try {
      const res = await dtpApi.requestOtp(phone.trim(), physicianId);
      if (res.success) {
        setStep('otp-verify');
      } else {
        setError(res.error || 'Failed to send verification code');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim() || !physicianId) return;
    setLoading(true);
    setError('');
    try {
      const res = await dtpApi.verifyOtp(phone.trim(), otpCode.trim(), physicianId);
      if (res.success && res.data) {
        if (res.data.isNewPatient) {
          transferTo(JSON.stringify([physicianId, res.data.token]));
          setSignupPending(res.data.token, physicianId, phone.trim());
          setStep('signup');
        } else {
          localStorage.setItem('dtp-token', res.data.token);
          setAuth(res.data.token, res.data.patient ?? ({} as any), physicianId);
          setStep('medical');
        }
      } else {
        setError(res.error || 'Invalid verification code');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  // --- Signup Handler ---

  const signupForm = useForm<SignupFormData>({
    defaultValues: lockedState ? { state: lockedState } : undefined
  });
  const signupFormDataRef = useRef<SignupFormData | null>(null);

  // BMI computation
  const bmiResult = useMemo(() => {
    const ft = parseInt(medical.heightFt, 10);
    const inches = parseInt(medical.heightIn, 10);
    const wt = parseFloat(medical.weightLbs);
    if (isNaN(ft) || isNaN(inches) || isNaN(wt) || wt <= 0) return null;
    const totalInches = ft * 12 + inches;
    if (totalInches <= 0) return null;
    const bmi = computeBmi(totalInches, wt);
    return { value: bmi, ...bmiCategory(bmi) };
  }, [medical.heightFt, medical.heightIn, medical.weightLbs]);

  const handleSignup = async (formData: SignupFormData) => {
    if (!formData.consentCompounding || !formData.consentTelehealth || !formData.consentHipaa || !formData.consentTos) {
      setError('All consent checkboxes are required');
      return;
    }
    // Validate age (18+) — human orders only. For a vet order the account holder
    // is the pet's owner and their DOB is optional/not gated.
    // Range-check whatever the date input produced before the age gate. A
    // two-digit year reads as an age in the thousands, so it clears 18+ and
    // would otherwise reach the pharmacy as the patient's DOB.
    const dobRangeMessage = dobRangeError(formData.dateOfBirth);
    if (dobRangeMessage) {
      setError(dobRangeMessage);
      return;
    }
    if (!isVet && formData.dateOfBirth) {
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      if (age < 18) {
        setError('You must be at least 18 years old to place an order');
        return;
      }
    }
    // Store signup form data and move to medical screening. The patient just
    // agreed to all four consents here (same texts the order requires), so
    // seed the review consents and flag the signup as done — the review step
    // then confirms rather than re-asking consents and address.
    setError('');
    signupFormDataRef.current = formData;
    setReviewConsents({ compounding: true, telehealth: true, hipaa: true, tos: true });
    setCompletedSignup(true);
    setStep('medical');
  };

  const validateMedical = (): string[] => {
    const errs: string[] = [];
    if (!qualificationReady) errs.push('Wait for qualification questions to load, or refresh to retry.');
    if (needsSex && !medical.biologicalSex) errs.push('Biological sex is required');
    if (needsMeasurements) {
      if (!medical.heightFt) errs.push('Height (feet) is required');
      if (medical.heightIn === '') errs.push('Height (inches) is required');
      if (!medical.weightLbs) errs.push('Weight is required');
    }
    for (const { key, question } of combinedQualificationQuestions(qualificationProducts, medical.biologicalSex)) {
      const answer = qualificationAnswers[key];
      if (!answer && question.required) errs.push(question.prompt);
      if (answer && question.detailsWhen === answer.value && !answer.details?.trim()) errs.push(question.detailsPrompt || 'Please provide details');
    }
    for (const p of intakePrograms) {
      const submission = programSubmission(p);
      if (p.dosagePlans?.length && submission.entry === "guided" && !submission.planId) errs.push("Review the guided dosage plan for " + p.name);
      errs.push(...programErrors(p, submission));
      if (entryFlow?.mode === "guided" && entryFlow.programIds.includes(p.id) && submission.entry !== "guided") errs.push("Complete guided assessment for " + p.name);
    }
    return errs;
  };

  const validateAnimal = (): string[] => {
    const errs: string[] = [];
    if (!animal.name.trim()) errs.push('Pet name is required');
    if (!animal.species) errs.push('Species is required');
    const wt = parseFloat(animal.weightLbs);
    if (!animal.weightLbs || isNaN(wt) || wt <= 0) errs.push('Weight (lbs) is required');
    return errs;
  };

  const buildAnimalPayload = () => ({
    name: animal.name.trim(),
    species: animal.species as AnimalSpeciesOpt,
    breed: animal.breed.trim() || undefined,
    sex: (animal.sex || undefined) as AnimalSexOpt | undefined,
    weightLbs: parseFloat(animal.weightLbs),
    ageYears: animal.ageYears ? parseFloat(animal.ageYears) : undefined,
  });

  const buildScreeningPayload = () => {
    const legacy: Record<string, boolean | string | number | undefined> = {};
    if (needsSex && medical.biologicalSex) legacy.biologicalSex = medical.biologicalSex;
    if (needsMeasurements) {
      legacy.heightInches = parseInt(medical.heightFt, 10) * 12 + parseInt(medical.heightIn, 10);
      legacy.weightLbs = parseFloat(medical.weightLbs);
      legacy.bmi = bmiResult?.value;
    }
    const legacyQuestions = defaultQualificationQuestions({ isPeptide: true });
    for (const submission of buildQualificationAnswers()) {
      for (const [id, answer] of Object.entries(submission.answers)) {
        const question = qualificationProducts.find(p => p._id === submission.productId)?.qualification.questions.find(q => q.id === id);
        if (!legacyQuestions.some(q => q.id === id && q.prompt === question?.prompt)) continue;
        legacy[id] = legacy[id] === true || answer.value;
        const detailKey = id === 'takingMedications' ? 'currentMedications' : id === 'hasAllergies' ? 'allergies' : null;
        if (detailKey) legacy[detailKey] = [...new Set([legacy[detailKey], answer.value ? answer.details : undefined].filter(Boolean))].join('; ');
      }
    }
    return legacy;
  };

  const handleMedicalSubmit = async () => {
    const errs = isVet ? validateAnimal() : validateMedical();
    if (errs.length > 0) { setMedicalErrors(errs); return; }
    setMedicalErrors([]);

    setLoading(true);
    setError('');
    try {
      if (isAuthenticated) {
        // Returning patient — update the animal (vet) or screening answers.
        const res = await dtpApi.updateScreening(
          isVet ? { animal: buildAnimalPayload() } : buildScreeningPayload()
        );
        if (res.success) {
          setStep('review');
        } else {
          setError(res.error || 'Failed to save your details');
        }
      } else {
        // New patient — submit signup with animal (vet) or screening data.
        const formData = signupFormDataRef.current!;
        const consents: ConsentType[] = ['compounding', 'telehealth', 'hipaa', 'tos'];
        const res = await dtpApi.signup(signupToken!, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          // Owner DOB is optional for vet orders; only send when provided.
          ...(formData.dateOfBirth ? { dateOfBirth: formData.dateOfBirth } : {}),
          phone: (storedPhone || phone)!,
          email: formData.email,
          address: {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
            ...(formData.street2 ? { street2: formData.street2 } : {})
          },
          ...(isVet
            ? { animal: buildAnimalPayload() }
            : { screeningAnswers: buildScreeningPayload() }),
          consents
        });

        if (res.success && res.data) {
          transferTo(JSON.stringify([physicianId, res.data.patient._id]));
          setAuth(res.data.token, res.data.patient, physicianId!);
          setStep('review');
        } else {
          setError(res.error || 'Signup failed');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save your details');
    } finally {
      setLoading(false);
    }
  };

  // --- Payment Handler ---

  const handleCreateOrder = async () => {
    // A cart edit may add new questions after the patient reached review.
    const intakeErrors = isVet ? validateAnimal() : validateMedical();
    if (intakeErrors.length) {
      setMedicalErrors(intakeErrors);
      setStep('medical');
      return;
    }
    // Validate consents
    if (!reviewConsents.compounding || !reviewConsents.telehealth || !reviewConsents.hipaa || !reviewConsents.tos) {
      setError('All consent checkboxes are required');
      return;
    }
    if (paymentProcessor && !processorAcknowledged) {
      setError(`Please acknowledge that your payment will be processed by ${paymentProcessor}`);
      return;
    }

    // Validate shipping address
    if (!shippingAddress.street.trim() || !shippingAddress.city.trim() || !shippingAddress.state.trim() || !shippingAddress.zipCode.trim()) {
      setError('Please complete your shipping address');
      return;
    }

    // Validate billing address if separate
    if (!billingSameAsShipping) {
      if (!billingAddress.street.trim() || !billingAddress.city.trim() || !billingAddress.state.trim() || !billingAddress.zipCode.trim()) {
        setError('Please complete your billing address');
        return;
      }
    }

    setLoading(true);
    setError('');
    try {
      const billing = billingSameAsShipping ? undefined : billingAddress;
      const consentsList = Object.entries(reviewConsents)
        .filter(([, checked]) => checked)
        .map(([key]) => key);
      if (paymentProcessor && processorAcknowledged) consentsList.push('payment_processor');
      const res = await dtpApi.createOrder(
        items.map(i => ({ productId: i.productId })),
        shippingAddress,
        billing,
        consentsList,
        isVet ? undefined : buildQualificationAnswers(),
        intakePrograms.map(programSubmission)
      );
      if (res.success && res.data) {
        setOrderCreated(true);
        setClientSecret(res.data.clientSecret);
        setOrderId(res.data.orderId);
        setTotalAmount(res.data.totalAmount);
        trackStoreEvent('order_created', { orderId: res.data.orderId });
        setStep('payment');

        const tenantConfig = getCurrentTenantConfig();
        const hyperswitchEnv = tenantConfig.payments.hyperswitch.environment;
        const customBackendUrl = hyperswitchEnv === 'sandbox'
          ? 'https://sandbox.hyperswitch.io'
          : 'https://api.hyperswitch.io';
        const hyperLoadPromise = loadHyper(tenantConfig.payments.hyperswitch.publishableKey, { customBackendUrl });
        setHyperPromise(hyperLoadPromise);
      } else {
        setError(res.error || 'Failed to create order');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  // Membership sign-in detour has no cart by design — render the OTP form.
  if (items.length === 0 && !orderCreated && !isSigninOnly) return null;

  return (
    <div className="max-w-2xl mx-auto">
      <button
        onClick={() => navigate(dtpPath(physicianId))}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to shop
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">{isPortalSignin ? 'Patient portal sign in' : 'Checkout'}</h1>

      {/* Step indicator */}
      {step !== 'payment' && !isSigninOnly && (
        <div className="flex items-center gap-2 mb-6 text-sm flex-wrap">
          {(() => {
            const steps: { key: CheckoutStep | 'otp'; label: string }[] = isAuthenticated
              ? [
                  { key: 'medical', label: isVet ? 'Pet Details' : 'Health Screening' },
                  { key: 'review', label: 'Review & Pay' },
                ]
              : [
                  { key: 'otp', label: 'Verify Phone' },
                  { key: 'signup', label: 'Your Info' },
                  { key: 'medical', label: isVet ? 'Pet Details' : 'Health Screening' },
                  { key: 'review', label: 'Review & Pay' },
                ];
            const currentIdx = steps.findIndex(s =>
              s.key === 'otp' ? ['otp-phone', 'otp-verify'].includes(step) : s.key === step
            );
            return steps.map((s, i) => (
              <span key={s.key} className="flex items-center gap-2">
                {i > 0 && <span className="text-gray-300">&rarr;</span>}
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  i < currentIdx ? 'bg-green-100 text-green-700'
                    : i === currentIdx ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {i + 1}. {s.label}
                </span>
              </span>
            ));
          })()}
        </div>
      )}

      {/* Order Summary — hidden during the membership sign-in detour (no cart by design) */}
      {!isSigninOnly && (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
        <div className="divide-y divide-gray-100">
          {items.map(item => (
            <div key={item.productId} className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-gray-900">{item.productName}</p>
                {intakePrograms.flatMap(p => {
                  const plan = p.dosagePlans?.find(plan => plan.id === programSubmission(p).planId && plan.productId === item.productId);
                  return plan ? [<p key={p.id} className="text-sm text-gray-600">Selected plan: {plan.doseLabel}{plan.prescriptionSig && <span className="block">{plan.prescriptionSig} Your clinician confirms the final prescription.</span>}</p>] : [];
                })}
                <p className="text-sm text-gray-500">Qty: 1</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-medium text-gray-900">${item.price.toFixed(2)}</span>
                {!orderCreated && (
                  <button
                    onClick={() => removeItem(item.productId)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-gray-200 pt-3 mt-3 space-y-1">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {orderCreated && totalAmount > subtotal && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Shipping</span>
              <span>${(totalAmount - subtotal).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-semibold text-gray-900 pt-1">
            <span>Total</span>
            <span>${(orderCreated ? totalAmount : subtotal).toFixed(2)}</span>
          </div>
        </div>
      </div>
      )}

      {/* --- OTP: Phone Step --- */}
      {step === 'otp-phone' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Verify Your Phone</h2>
          <p className="text-sm text-gray-500 mb-4">Enter your phone number to continue.</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyDown={e => e.key === 'Enter' && handleRequestOtp()}
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              onClick={handleRequestOtp}
              disabled={loading || !phone.trim()}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Send Verification Code
            </button>
          </div>
        </div>
      )}

      {/* --- OTP: Verify Step --- */}
      {step === 'otp-verify' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Enter Verification Code</h2>
          <p className="text-sm text-gray-500 mb-4">
            We sent a 6-digit code to <span className="font-medium">{phone}</span>
          </p>
          <div className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              className="w-full text-center text-2xl tracking-[0.5em] py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              onClick={handleVerifyOtp}
              disabled={loading || otpCode.length < 6}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Verify
            </button>
            <button
              onClick={() => { setStep('otp-phone'); setOtpCode(''); setError(''); }}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              Use a different phone number
            </button>
          </div>
        </div>
      )}

      {/* --- Signup Step --- */}
      {step === 'signup' && (
        <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{isVet ? 'Owner Information' : 'Personal Information'}</h2>
            {isVet && (
              <p className="text-sm text-gray-500 -mt-2 mb-4">Enter the pet owner’s details. We’ll ask about the pet next.</p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  {...signupForm.register('firstName', { required: 'Required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {signupForm.formState.errors.firstName && <p className="text-xs text-red-600 mt-1">{signupForm.formState.errors.firstName.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  {...signupForm.register('lastName', { required: 'Required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth {isVet ? '(optional)' : '*'}
              </label>
              <input
                type="date"
                {...dobInputBounds()}
                {...signupForm.register('dateOfBirth', { required: isVet ? false : 'Required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                {...signupForm.register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {signupForm.formState.errors.email && <p className="text-xs text-red-600 mt-1">{signupForm.formState.errors.email.message}</p>}
              <p className="text-xs text-gray-500 mt-1">Used for receipts, shipping updates, and recovery.</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping Address</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                <input
                  {...signupForm.register('street', { required: 'Required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apt/Suite</label>
                <input
                  {...signupForm.register('street2')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                  <input
                    {...signupForm.register('city', { required: 'Required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                  {multiStateAllowed ? (
                    <select
                      {...signupForm.register('state', { required: 'Required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">State</option>
                      {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input
                      {...signupForm.register('state', { required: 'Required' })}
                      maxLength={2}
                      readOnly={!!lockedState}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        lockedState
                          ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed'
                          : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      title={lockedState ? 'Ships to your provider’s licensed state' : undefined}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP *</label>
                  <input
                    {...signupForm.register('zipCode', { required: 'Required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckSquare className="w-5 h-5" />
              Required Consents
            </h2>
            <div className="space-y-4">
              {[
                { key: 'consentCompounding' as const, label: 'Compounding Pharmacy Disclaimer', text: DEFAULT_CONSENT_TEXTS.compounding },
                { key: 'consentTelehealth' as const, label: 'Telehealth Consent', text: DEFAULT_CONSENT_TEXTS.telehealth },
                { key: 'consentHipaa' as const, label: 'HIPAA Acknowledgment', text: DEFAULT_CONSENT_TEXTS.hipaa },
                { key: 'consentTos' as const, label: 'Terms of Service', text: DEFAULT_CONSENT_TEXTS.tos }
              ].map(consent => (
                <label key={consent.key} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    {...signupForm.register(consent.key, { required: true })}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">{consent.label}</span>
                    <p className="text-xs text-gray-500 mt-0.5">{consent.text}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isVet ? 'Continue to Pet Details' : 'Continue to Health Screening'}
          </button>
        </form>
      )}

      {/* --- Medical Screening Step --- */}
      {step === 'medical' && (
        <div className="space-y-6">
          {isVet && (
            <AnimalDetailsCard animal={animal} setAnimal={setAnimal} />
          )}
          {!isVet && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Health Screening</h2>
            <p className="text-sm text-gray-500 mb-6">Answer all questions honestly. This information is protected under HIPAA.</p>

            {needsSex && <>
            {/* Biological Sex */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Biological sex <span className="text-red-500">*</span></label>
              <div className="flex gap-3">
                {(['male', 'female'] as const).map(sex => (
                  <button
                    key={sex}
                    type="button"
                    onClick={() => setMedical(m => ({ ...m, biologicalSex: sex }))}
                    className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                      medical.biologicalSex === sex
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 bg-gray-50 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {sex.charAt(0).toUpperCase() + sex.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            </>}
            {needsMeasurements && <>
            {/* Height & Weight */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Height <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <select
                    value={medical.heightFt}
                    onChange={e => setMedical(m => ({ ...m, heightFt: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">ft</option>
                    {[4, 5, 6, 7].map(n => <option key={n} value={String(n)}>{n}</option>)}
                  </select>
                  <select
                    value={medical.heightIn}
                    onChange={e => setMedical(m => ({ ...m, heightIn: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">in</option>
                    {Array.from({ length: 12 }, (_, i) => <option key={i} value={String(i)}>{i}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (lbs) <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  value={medical.weightLbs}
                  onChange={e => setMedical(m => ({ ...m, weightLbs: e.target.value }))}
                  placeholder="165"
                  min="50"
                  max="600"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            {/* BMI Display */}
            {bmiResult && (
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-5">
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-medium">{bmiResult.value.toFixed(1)}</span>
                  <span className="text-sm text-gray-500">BMI</span>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${bmiResult.color}`}>
                  {bmiResult.label}
                </span>
              </div>
            )}

            </>}
            {intakePrograms.map(p => <section key={p.id} className="rounded-xl border p-5 mb-6 space-y-4">
              <h3 className="font-semibold">{p.name} — required assessment</h3>
              {!p.active ? <p className="text-red-700">This program is unavailable. Contact your clinic.</p> : <>
                {entryFlow?.mode !== "direct" && entryFlow?.programIds.includes(p.id) && <button type="button" className="text-blue-700 underline" onClick={() => navigate(dtpPath(physicianId, `/intake/${p.id}`))}>Review guided recommendation</button>}
                <ProgramAssessmentFields program={p} submission={programSubmission(p)} productIds={items.map(item => item.productId)} onChange={value => setProgramDrafts(current => ({ ...current, [p.id]: value }))} />
              </>}
            </section>)}
            <ProductQualificationQuestions products={qualificationProducts} answers={qualificationAnswers} sex={medical.biologicalSex} onChange={(key, answer) => setQualificationAnswers(current => ({ ...current, [key]: answer }))} />
            {!qualificationReady && <p role="status" className="py-4 text-sm">Loading qualification questions…</p>}

          </div>
          )}

          {medicalErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
                {medicalErrors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex justify-between">
            {!isAuthenticated ? (
              <button
                type="button"
                onClick={() => { setStep('signup'); setMedicalErrors([]); setError(''); }}
                className="flex items-center gap-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate(dtpPath(physicianId))}
                className="flex items-center gap-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Shop
              </button>
            )}
            <button
              type="button"
              onClick={handleMedicalSubmit}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Continue to Review
            </button>
          </div>
        </div>
      )}

      {/* --- Review Step (authenticated) --- */}
      {step === 'review' && (
        <div className="space-y-6">
          {/* Shipping Address — a new patient sees a summary of what they
              entered at signup with an Edit affordance; a returning patient
              edits it inline. */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Shipping Address</h2>
              {completedSignup && shippingComplete && !editingShipping && (
                <button
                  type="button"
                  onClick={() => setEditingShipping(true)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  Edit
                </button>
              )}
            </div>
            {completedSignup && shippingComplete && !editingShipping ? (
              <div className="text-sm text-gray-700 leading-relaxed">
                <p>{shippingAddress.street}{shippingAddress.street2 ? `, ${shippingAddress.street2}` : ''}</p>
                <p>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zipCode}</p>
              </div>
            ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                <input
                  value={shippingAddress.street}
                  onChange={e => setShippingAddress(a => ({ ...a, street: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apt/Suite</label>
                <input
                  value={shippingAddress.street2}
                  onChange={e => setShippingAddress(a => ({ ...a, street2: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                  <input
                    value={shippingAddress.city}
                    onChange={e => setShippingAddress(a => ({ ...a, city: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                  {multiStateAllowed ? (
                    <select
                      value={shippingAddress.state}
                      onChange={e => setShippingAddress(a => ({ ...a, state: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">State</option>
                      {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input
                      value={shippingAddress.state}
                      onChange={e => !lockedState && setShippingAddress(a => ({ ...a, state: e.target.value }))}
                      maxLength={2}
                      readOnly={!!lockedState}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        lockedState
                          ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed'
                          : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      title={lockedState ? 'Ships to your provider’s licensed state' : undefined}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP *</label>
                  <input
                    value={shippingAddress.zipCode}
                    onChange={e => setShippingAddress(a => ({ ...a, zipCode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Billing Address */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={billingSameAsShipping}
                onChange={e => {
                  const same = e.target.checked;
                  setBillingSameAsShipping(same);
                  // When switching to a separate billing address, seed it from
                  // the shipping address so the patient only edits what differs
                  // instead of retyping the whole thing. Don't clobber values
                  // they've already entered if they toggle back and forth.
                  if (!same) {
                    setBillingAddress(b =>
                      (b.street || b.city || b.state || b.zipCode) ? b : { ...shippingAddress }
                    );
                  }
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-900">Billing address same as shipping</span>
            </label>

            {!billingSameAsShipping && (
              <div className="mt-4 space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">Billing Address</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                  <input
                    value={billingAddress.street}
                    onChange={e => setBillingAddress(a => ({ ...a, street: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Apt/Suite</label>
                  <input
                    value={billingAddress.street2}
                    onChange={e => setBillingAddress(a => ({ ...a, street2: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input
                      value={billingAddress.city}
                      onChange={e => setBillingAddress(a => ({ ...a, city: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <input
                      value={billingAddress.state}
                      onChange={e => setBillingAddress(a => ({ ...a, state: e.target.value }))}
                      maxLength={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP *</label>
                    <input
                      value={billingAddress.zipCode}
                      onChange={e => setBillingAddress(a => ({ ...a, zipCode: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Consents — a new patient already agreed to these at signup (same
              consent texts, carried into the order), so confirm rather than
              re-ask. A returning patient skipped signup and checks them here. */}
          {completedSignup ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <CheckSquare className="w-5 h-5 text-green-600 shrink-0" />
                <span>You agreed to the required consents during sign-up.</span>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CheckSquare className="w-5 h-5" />
                Required Consents
              </h2>
              <div className="space-y-4">
                {([
                  { key: 'compounding' as const, label: 'Compounding Pharmacy Disclaimer', text: DEFAULT_CONSENT_TEXTS.compounding },
                  { key: 'telehealth' as const, label: 'Telehealth Consent', text: DEFAULT_CONSENT_TEXTS.telehealth },
                  { key: 'hipaa' as const, label: 'HIPAA Acknowledgment', text: DEFAULT_CONSENT_TEXTS.hipaa },
                  { key: 'tos' as const, label: 'Terms of Service', text: DEFAULT_CONSENT_TEXTS.tos }
                ]).map(consent => (
                  <label key={consent.key} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reviewConsents[consent.key]}
                      onChange={e => setReviewConsents(c => ({ ...c, [consent.key]: e.target.checked }))}
                      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">{consent.label}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{consent.text}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {paymentProcessor && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={processorAcknowledged}
                  onChange={e => setProcessorAcknowledged(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Payment Processing</span>
                  <p className="text-xs text-gray-500 mt-0.5">I understand that my payment for this order will be processed by {paymentProcessor}.</p>
                </div>
              </label>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            onClick={handleCreateOrder}
            disabled={loading || (!!paymentProcessor && !processorAcknowledged)}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Proceed to Payment
          </button>
        </div>
      )}

      {/* --- Payment Step --- */}
      {step === 'payment' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Payment</h2>
          {paymentProcessor
            ? <p className="text-sm text-gray-500 mb-4">Payment processed by {paymentProcessor}</p>
            : <div className="mb-3" />}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          {hyperPromise && clientSecret ? (
            <HyperElements
              hyper={hyperPromise}
              options={{
                publishableKey: getCurrentTenantConfig().payments.hyperswitch.publishableKey,
                clientSecret,
                loader: 'auto',
                appearance: { theme: 'default' as any }
              }}
            >
              <CheckoutForm physicianId={physicianId!} orderId={orderId} totalAmount={totalAmount} />
            </HyperElements>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CheckoutForm({ physicianId, orderId, totalAmount }: { physicianId: string; orderId: string; totalAmount: number }) {
  const returnUrl = `${window.location.origin}${dtpPath(physicianId, `/orders/${orderId}/confirmation`)}`;

  return (
    <UnifiedCheckout
      id="dtp-checkout"
      options={{
        layout: { type: 'tabs' },
        paymentMethodOrder: ['card', 'google_pay', 'apple_pay'],
        fields: {
          billingDetails: {
            name: 'auto',
            email: 'auto',
            phone: 'auto',
            address: 'never'
          }
        },
        sdkHandleConfirmPayment: {
          handleConfirm: true,
          buttonText: `Pay $${totalAmount.toFixed(2)}`,
          confirmParams: { return_url: returnUrl }
        },
        wallets: {
          walletReturnUrl: returnUrl,
          applePay: 'auto',
          googlePay: 'auto'
        },
        displaySavedPaymentMethods: false
      } as any}
    />
  );
}

function AnimalDetailsCard({
  animal, setAnimal
}: {
  animal: AnimalDetailsData;
  setAnimal: Dispatch<SetStateAction<AnimalDetailsData>>;
}) {
  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-green-500 focus:border-green-500';
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Pet Details</h2>
      <p className="text-sm text-gray-500 mb-6">Tell us about the animal this prescription is for. Weight is required for accurate dosing.</p>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pet name <span className="text-red-500">*</span></label>
          <input
            value={animal.name}
            onChange={e => setAnimal(a => ({ ...a, name: e.target.value }))}
            placeholder="e.g., Rex"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Species <span className="text-red-500">*</span></label>
          <select
            value={animal.species}
            onChange={e => setAnimal(a => ({ ...a, species: e.target.value as AnimalSpeciesOpt }))}
            className={inputCls}
          >
            <option value="">Select…</option>
            {SPECIES_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Weight (lbs) <span className="text-red-500">*</span></label>
          <input
            type="number"
            value={animal.weightLbs}
            onChange={e => setAnimal(a => ({ ...a, weightLbs: e.target.value }))}
            placeholder="e.g., 68"
            min="0"
            step="0.1"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Age (years)</label>
          <input
            type="number"
            value={animal.ageYears}
            onChange={e => setAnimal(a => ({ ...a, ageYears: e.target.value }))}
            placeholder="e.g., 5"
            min="0"
            step="0.1"
            className={inputCls}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Breed</label>
          <input
            value={animal.breed}
            onChange={e => setAnimal(a => ({ ...a, breed: e.target.value }))}
            placeholder="e.g., Labrador"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sex</label>
          <select
            value={animal.sex}
            onChange={e => setAnimal(a => ({ ...a, sex: e.target.value as AnimalSexOpt }))}
            className={inputCls}
          >
            <option value="">Select…</option>
            {ANIMAL_SEX_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}
